import React, { useState } from "react";
import { toast } from "sonner";
import api, { formatRp, notifyError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { useIsFeatureEnabled } from "@/hooks/useFeatureFlag";
import { canExtendDeadline, deadlineNearOrPast, serverOffsetMs } from "@/lib/paymentDeadline";

/* Phase 3 — keputusan pengajuan pembatalan. Eksekusi = endpoint Phase 1 (approve/reject);
   pembatalan + refund dijalankan backend via P0 cancel_order (tidak ada logika duplikat di UI). */

const PaymentChoice = ({ value, onChange }) => (
  <fieldset className="space-y-2" data-testid="approve-payment-choice">
    <legend className="text-sm font-semibold">Apakah dana pesanan ini sudah masuk ke rekening/QRIS toko?</legend>
    <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900" data-testid="approve-check-bank-warning">
      <b>Wajib periksa mutasi bank / riwayat QRIS</b> sebelum memilih. Keputusan ini menentukan apakah refund manual dicatat.
    </div>
    {[
      { v: true, id: "yes", title: "Ya, dana sudah diterima", desc: "Pembayaran dikonfirmasi tanpa bukti → pesanan dibatalkan → refund manual PENDING dicatat." },
      { v: false, id: "no", title: "Tidak, dana belum diterima", desc: "Pesanan dibatalkan tanpa refund (bukti menunggu, bila ada, ditolak)." },
    ].map((opt) => (
      <label key={opt.id} className={`flex gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${value === opt.v ? "border-[#E85D04] bg-[#FFEDD5]/50" : "border-[#E5E5E5] hover:bg-[#FAF9F6]"}`}>
        <input type="radio" name="payment_received" className="mt-0.5" checked={value === opt.v} onChange={() => onChange(opt.v)}
          data-testid={`approve-payment-${opt.id}`} />
        <span className="text-sm"><span className="font-semibold block">{opt.title}</span><span className="text-xs text-[#5C5C5C]">{opt.desc}</span></span>
      </label>
    ))}
  </fieldset>
);

export function ApproveCancellationDialog({ open, order, onClose, onDone }) {
  const [paymentReceived, setPaymentReceived] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  if (!order) return null;
  const verified = order.payment_status === "verified";
  const amount = order.final_total ?? order.total;
  const refundWillBeCreated = verified || paymentReceived === true;
  const canSubmit = !busy && (verified || paymentReceived !== null);
  const reset = () => { setPaymentReceived(null); setNote(""); };
  const submit = async () => {
    setBusy(true);
    try {
      const body = { note: note.trim() || null };
      if (!verified) body.payment_received = paymentReceived;
      await api.post(`/admin/orders/${order.id}/cancellation/approve`, body);
      toast.success(`Pembatalan #${order.order_number} disetujui`);
      reset(); onDone?.();
    } catch (e) { notifyError(e); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose(); } }}>
      <DialogContent className="max-w-lg" data-testid="approve-cancellation-dialog">
        <DialogHeader>
          <DialogTitle>Setujui Pembatalan #{order.order_number}</DialogTitle>
          <DialogDescription>Pesanan akan dibatalkan dan stok dikembalikan. Alasan pelanggan: “{order.cancellation?.request_reason || "-"}”</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {verified ? (
            <div className="rounded-xl bg-yellow-50 border border-yellow-200 p-3 text-sm text-yellow-800" data-testid="approve-verified-refund-warning">
              Pembayaran <b>sudah terverifikasi</b>. Menyetujui akan mencatat kewajiban <b>refund manual PENDING</b> sebesar <b>{formatRp(amount)}</b>. Dynicty tidak mentransfer dana — Anda mengembalikannya sendiri lalu mencatatnya di menu Refund.
            </div>
          ) : <PaymentChoice value={paymentReceived} onChange={setPaymentReceived} />}
          {!verified && refundWillBeCreated && (
            <div className="rounded-xl bg-yellow-50 border border-yellow-200 p-3 text-xs text-yellow-800" data-testid="approve-refund-will-be-created">
              Refund manual PENDING sebesar <b>{formatRp(amount)}</b> akan dicatat setelah pembatalan.
            </div>
          )}
          <div>
            <label className="text-sm font-medium" htmlFor="approve-note">Catatan (opsional)</label>
            <Textarea id="approve-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="rounded-xl mt-1" data-testid="approve-note-input" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" className="rounded-xl" onClick={() => { reset(); onClose(); }} disabled={busy} data-testid="approve-dialog-close">Tutup</Button>
          <Button className="rounded-xl brand-btn-primary" onClick={submit} disabled={!canSubmit} data-testid="approve-dialog-confirm">
            {busy ? "Memproses..." : "Setujui & Batalkan Pesanan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RejectCancellationDialog({ open, order, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const deadlineFeatureOn = useIsFeatureEnabled("payments.manual_deadline");
  if (!order) return null;
  const valid = reason.trim().length >= 3;
  const nowMs = Date.now() + serverOffsetMs(order.server_now);
  const showDeadlineWarning = deadlineNearOrPast(order, nowMs) && order.status === "pending_payment"
    && !["pending_verification", "verified", "paid"].includes(order.payment_status);
  const extendAvailable = deadlineFeatureOn && canExtendDeadline(order);
  const submit = async (extend) => {
    setBusy(true);
    try {
      await api.post(`/admin/orders/${order.id}/cancellation/reject`, { reason: reason.trim(), extend_payment_deadline: !!extend });
      toast.success(extend ? `Pengajuan #${order.order_number} ditolak · waktu bayar +60 menit` : `Pengajuan #${order.order_number} ditolak`);
      setReason(""); onDone?.();
    } catch (e) { notifyError(e); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setReason(""); onClose(); } }}>
      <DialogContent className="max-w-md" data-testid="reject-cancellation-dialog">
        <DialogHeader>
          <DialogTitle>Tolak Pengajuan #{order.order_number}</DialogTitle>
          <DialogDescription>Pesanan tetap berjalan; tidak ada perubahan pembayaran, stok, voucher, atau pengiriman. Alasan akan ditampilkan ke pelanggan.</DialogDescription>
        </DialogHeader>
        {showDeadlineWarning && (
          <div className="rounded-xl bg-amber-50 border border-amber-300 p-3 text-xs text-amber-900" role="alert" data-testid="reject-deadline-warning">
            <b>Batas waktu pembayaran pesanan ini sudah/akan segera berakhir.</b> Jika pembatalan ditolak, sistem dapat membatalkan pesanan otomatis.
          </div>
        )}
        <label className="text-sm font-medium" htmlFor="reject-reason">Alasan penolakan (wajib, min. 3 karakter)</label>
        <Textarea id="reject-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} className="rounded-xl" data-testid="reject-reason-input" />
        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="outline" className="rounded-xl" onClick={() => { setReason(""); onClose(); }} disabled={busy} data-testid="reject-dialog-close">Tutup</Button>
          <Button className="rounded-xl bg-red-600 hover:bg-red-700 text-white" onClick={() => submit(false)} disabled={!valid || busy} data-testid="reject-dialog-confirm">
            {busy ? "Menyimpan..." : "Tolak Pembatalan"}
          </Button>
          {extendAvailable && (
            <Button variant="outline" className="rounded-xl border-[#E85D04] text-[#E85D04] hover:bg-[#FFEDD5]" onClick={() => submit(true)} disabled={!valid || busy} data-testid="reject-extend-confirm">
              Tolak & Beri Waktu 60 Menit Lagi
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
